/* eslint-disable ember/no-computed-properties-in-native-classes */
import { assert } from '@ember/debug';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, computed, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { PrismIcon, PrismTooltip } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import PowerCalendarDays from 'ember-power-calendar/components/power-calendar/days';
import {
  add,
  endOf,
  endOfWeek,
  isBefore,
  startOf,
  startOfWeek,
} from 'ember-power-calendar/utils';
import { and, or } from 'ember-truth-helpers';
import HotelMarketInsightTooltipsTooltipSummary from 'lumen/components/hotel/market-insight/tooltips/tooltip-summary';
import marketInsightEmberPowerCalendarDayClassesCustom from 'lumen/helpers/market-insight-ember-power-calendar-day-classes-custom';
import type { Day } from 'lumen/models';
import { CALENDAR_FILTERS } from 'lumen/utils/constants/market-insight-constants';
import { timeout } from 'lumen/utils/waiters';

import styles from './day-market-demand.module.css';

/**
 * Copied over from a private util function in ember-power-select because we require it to overwrite a method
 * https://github.com/cibernox/ember-power-calendar/blob/v1.1.0/ember-power-calendar/src/-private/days-utils.ts#L56-L59
 */
// @ts-expect-error: Incorrect type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function firstDay(currentCenter, localeStartOfWeek) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const firstDayOfMonth = startOf(currentCenter, 'month');
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return startOfWeek(firstDayOfMonth, localeStartOfWeek);
}

/**
 * Copied over from a private util function in ember-power-select because we require it to overwrite a method
 * https://github.com/cibernox/ember-power-calendar/blob/v1.1.0/ember-power-calendar/src/-private/days-utils.ts#L233-L240
 */
// @ts-expect-error: Incorrect type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function lastDay(localeStartOfWeek, currentCenter) {
  assert(
    'The center of the calendar is an invalid date.',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    !Number.isNaN(currentCenter.getTime()),
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const lastDayOfMonth = endOf(currentCenter, 'month');
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return endOfWeek(lastDayOfMonth, localeStartOfWeek);
}

/*
Custom day component to handle market insight demand calendar needs better:

original component:
- https://github.com/cibernox/ember-power-calendar/blob/v0.12.0/addon/components/power-calendar/days.js
*/

export default class HotelMarketInsightCalendarsDaysDayMarketDemand extends PowerCalendarDays {
  @service declare miRateindex: Services['mi-rateindex'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  styles = styles;

  /**
   * We track hovers over the calendar day here
   * If a user remains here for 2 seconds we'll log an event
   * If they leave sooner, the event will be cancelled
   */
  trackHoverTask = task({ restartable: true }, async (day) => {
    await timeout('extra-long');

    // Testing out the new tracking service, eventually one of these ⬆️⬇️ tracking calls will be removed
    this.usageAnalytics.track('Daily Market Demand Previewed', {
      eventTrigger: 'calendar-sidebar-hover',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      daysOffsetFromToday: day.moment.diff(dayjs().startOf('day'), 'day'),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isOutlier: day.hasPriceHighOutlier || day.hasPriceLowOutlier,
    });
  });

  @computed('args.selectedCalendarViewFilterIds.length')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calendarViewFilters() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const hasNoFilters = this.args.selectedCalendarViewFilterIds.length === 0;

    return {
      hasNoFilters,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      hasDemandIncreaseFilter: this.args.selectedCalendarViewFilterIds.includes(
        CALENDAR_FILTERS.DEMAND_INCREASE,
      ),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      hasDemandDecreaseFilter: this.args.selectedCalendarViewFilterIds.includes(
        CALENDAR_FILTERS.DEMAND_DECREASE,
      ),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      hasPriceHighFilter: this.args.selectedCalendarViewFilterIds.includes(
        CALENDAR_FILTERS.PRICE_HIGH,
      ),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      hasPriceLowFilter: this.args.selectedCalendarViewFilterIds.includes(
        CALENDAR_FILTERS.PRICE_LOW,
      ),
    };
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get days() {
    const today = this.powerCalendar.getDate();
    const theLastDay = lastDay(this.localeStartOfWeek, this.currentCenter);
    let day = firstDay(this.currentCenter, this.localeStartOfWeek);
    const days = [];
    while (isBefore(day, theLastDay)) {
      days.push(this.buildDay(day, today));
      day = add(day, 1, 'day');
    }
    return days;
  }

  // Methods

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildDay(date, today) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const dateMoment = dayjs(date);
    const id = dateMoment.format('YYYY-MM-DD');
    const dayFromStore = this.store.peekRecord<Day>('day', id);

    let destinationDemandIndex = null;
    let demandIndex = null;
    let summaryDemandLevelColorScaleNumber = null;
    let isDestinationDemandIndexMissing = false;

    // Properties we can filter on in the calendar
    let hasDemandIncrease = false;
    let hasDemandDecrease = false;
    let hasPriceHighOutlier = false;
    let hasPriceLowOutlier = false;

    // Get the (most recent) demand index from the summary in the store.
    if (dayFromStore?.mostRecentDailySummary) {
      ({
        // @ts-expect-error: Incorrect type
        destinationDemandIndex,
        // @ts-expect-error: Incorrect type
        demandIndex,
        // @ts-expect-error: Incorrect type
        isDestinationDemandIndexMissing,
      } = dayFromStore.mostRecentDailySummary);

      const {
        // @ts-expect-error: Incorrect type
        demandLevelColorScaleNumber,
        // @ts-expect-error: Incorrect type
        demandLevelColorScaleNumberRefPeriod,
        // @ts-expect-error: Incorrect type
        hasDemandChange,
      } = dayFromStore.mostRecentDailySummary;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      summaryDemandLevelColorScaleNumber = demandLevelColorScaleNumber;

      // check if there is bucket change vs. the reference day
      if (hasDemandChange) {
        const isIncrease =
          summaryDemandLevelColorScaleNumber >
          demandLevelColorScaleNumberRefPeriod;
        hasDemandIncrease = isIncrease;
        hasDemandDecrease = !isIncrease;
      }
    }

    // @ts-expect-error: Incorrect type
    if (dayFromStore?.outliers?.length > 0) {
      // @ts-expect-error: Incorrect type
      const { value } = dayFromStore.outliers[0];
      hasPriceHighOutlier = value > 0;
      hasPriceLowOutlier = value < 0;
    }

    return {
      id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      number: date.getDate(),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      date: new Date(date),
      isHovered: false,
      isDisabled: this.dayIsDisabled(date),
      isFocused: this.focusedId === id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      isCurrentMonth: date.getMonth() === this.currentCenter.getMonth(),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      isToday: dateMoment.isSame(today, 'day'),
      // Custom day properties added by us
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      rateIndexLevelNumber: summaryDemandLevelColorScaleNumber,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      destinationDemandIndex,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      demandIndex,
      day: dayFromStore,
      moment: dateMoment,
      rateindex:
        this.miRateindex.getRateIndexForCurrentHotelAndLatestExtractDate(
          // @ts-expect-error: Incorrect type
          dayFromStore,
        ),
      hasMarketData: !isDestinationDemandIndexMissing,
      hasDemandChange: hasDemandIncrease || hasDemandDecrease,
      hasDemandIncrease,
      hasDemandDecrease,
      hasPriceOutlier: hasPriceHighOutlier || hasPriceLowOutlier,
      hasPriceHighOutlier,
      hasPriceLowOutlier,
    };
  }

  /**
   * Overwritten cause EPC compares hours as well
   * Changed it so we just check the day
   * @param { Date} date : current date being checked
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  dayIsDisabled(date) {
    const isDisabled = !this.args.calendar.actions.select;
    if (isDisabled) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (this.args.minDate && dayjs(date).isBefore(this.args.minDate, 'day')) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (this.args.maxDate && dayjs(date).isAfter(this.args.maxDate, 'day')) {
      return true;
    }

    return false;
  }

  // @ts-expect-error: Incorrect type
  @action onMouseEnter(day): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (day.isDisabled) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    set(day, 'isHovered', true);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.trackHoverTask.perform(day);
  }

  // @ts-expect-error: Incorrect type
  @action onMouseLeave(day): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (day.isDisabled) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    set(day, 'isHovered', false);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.trackHoverTask.cancelAll();
  }

  // @ts-expect-error: Incorrect type
  @action triggerSegmentDayTracking(day): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (day.isDisabled) {
      return;
    }

    this.usageAnalytics.track('Daily Market Demand Viewed', {
      eventTrigger: 'calendar-sidebar',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      daysOffsetFromToday: day.moment
        .startOf('day')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .diff(dayjs().startOf('day'), 'days'),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      isOutlier: day.hasPriceHighOutlier || day.hasPriceLowOutlier,
    });
  }

  <template>
    {{! @glint-nocheck }}
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class={{concat "ember-power-calendar-days" " " this.styles.container}}
      data-power-calendar-id={{or
        @calendar.calendarUniqueId
        @calendar.uniqueId
      }}
      {{on "click" this.handleClick}}
      ...attributes
    >
      <div class="ember-power-calendar-row ember-power-calendar-weekdays">
        {{#each this.weekdaysNames as |wdn|}}
          <div class="ember-power-calendar-weekday">
            {{wdn}}
          </div>
        {{/each}}
      </div>

      <div
        class="ember-power-calendar-day-grid"
        {{on "keydown" this.handleKeyDown}}
      >
        {{#each this.weeks key="id" as |week|}}
          <div
            class="ember-power-calendar-row ember-power-calendar-week"
            data-missing-days={{week.missingDays}}
          >
            {{#each week.days key="id" as |day|}}
              <button
                {{! TODO: Remove this helper and define more local classes }}
                class={{marketInsightEmberPowerCalendarDayClassesCustom
                  day
                  @calendar
                  this.weeks
                  @dayClass
                  this.calendarViewFilters
                  @sidebarCalendarRange
                }}
                data-date={{day.id}}
                disabled={{day.isDisabled}}
                type="button"
                {{on "click" (fn this.triggerSegmentDayTracking day)}}
                {{on "mouseenter" (fn this.onMouseEnter day)}}
                {{on "mouseleave" (fn this.onMouseLeave day)}}
              >
                <div class={{this.styles.selected-day-square}}></div>

                {{#if (and @isMarketInsightFull day.hasPriceOutlier)}}
                  {{#if this.calendarViewFilters.hasNoFilters}}
                    <div class={{this.styles.outlier}}></div>
                  {{else if
                    (and
                      day.hasPriceHighOutlier
                      this.calendarViewFilters.hasPriceHighFilter
                    )
                  }}
                    <div
                      class={{this.styles.outlier}}
                      data-test-id="day-market-demand-high-price"
                    ></div>
                  {{else if
                    (and
                      day.hasPriceLowOutlier
                      this.calendarViewFilters.hasPriceLowFilter
                    )
                  }}
                    <div
                      class={{this.styles.outlier}}
                      data-test-id="day-market-demand-low-price"
                    ></div>
                  {{/if}}
                {{/if}}

                <span class={{this.styles.day-number}}>
                  {{day.number}}
                </span>

                {{#if day.hasDemandChange}}
                  {{#if
                    (and
                      day.hasDemandIncrease
                      (or
                        this.calendarViewFilters.hasNoFilters
                        this.calendarViewFilters.hasDemandIncreaseFilter
                      )
                    )
                  }}
                    <span
                      class={{this.styles.change-icon}}
                      data-test-id="demand-change-icon-increase"
                    >
                      <PrismIcon
                        @color="inherit"
                        @name="arrow-up"
                        @size="extra-small"
                      />
                    </span>
                  {{else if
                    (and
                      day.hasDemandDecrease
                      (or
                        this.calendarViewFilters.hasNoFilters
                        this.calendarViewFilters.hasDemandDecreaseFilter
                      )
                    )
                  }}
                    <span
                      class={{this.styles.change-icon}}
                      data-test-id="demand-change-icon-decrease"
                    >
                      <PrismIcon
                        @color="inherit"
                        @name="arrow-down"
                        @size="extra-small"
                      />
                    </span>
                  {{/if}}
                {{/if}}

                {{#if day.hasMarketData}}
                  {{#if day.isHovered}}
                    <EmberTooltip
                      @isShown={{true}}
                      @side="right"
                      @tooltipClass="base-tooltip tooltip-market-insight"
                    >
                      <HotelMarketInsightTooltipsTooltipSummary
                        @day={{day.day}}
                        @isDailyTooltip={{true}}
                        @isMarketInsightFull={{@isMarketInsightFull}}
                        @rateindex={{day.rateindex}}
                        @refDaysAgo={{@refDaysAgo}}
                      />
                    </EmberTooltip>
                  {{/if}}
                {{else}}
                  <PrismTooltip
                    @text={{t
                      "market_insight.calendar_tooltip.no_data_message"
                    }}
                  />
                {{/if}}
              </button>
            {{/each}}
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}
